import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { todayLocal } from '../../utils/format';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Skeleton,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import CuitInput from '../../components/CuitInput';
import { useEmpresa, useCreateEmpresa, useUpdateEmpresa } from '../../hooks/useEmpresas';
import { validateCuit } from '../../utils/validation';
import {
  CONDICION_IVA_OPTIONS,
  ESTADO_EMPRESA_OPTIONS,
  type CondicionIva,
  type EstadoEmpresa,
} from '../../types/empresa';

// ----- Schema Zod (#031) -----
// Notas:
//  - CUIT: validamos con algoritmo AFIP (validateCuit ya existe).
//  - Strings con trim para evitar "  Razón Social  " en BD.
//  - Email: solo formato si viene, trim+lowercase si no vacío.
//  - Fecha de inicio: si viene, no puede ser futura.
const hoyIso = () => todayLocal();

const schema = z.object({
  cuit: z
    .string({ required_error: 'CUIT es requerido' })
    .trim()
    .min(1, 'CUIT es requerido')
    .refine((v) => validateCuit(v), 'CUIT inválido (dígito verificador AFIP no coincide)'),
  razon_social: z
    .string({ required_error: 'Razón social es requerida' })
    .trim()
    .min(2, 'Razón social debe tener al menos 2 caracteres')
    .max(200, 'Razón social no puede superar 200 caracteres'),
  domicilio: z
    .string()
    .trim()
    .max(300, 'Domicilio no puede superar 300 caracteres')
    .optional()
    .or(z.literal('')),
  condicion_iva: z
    .enum(['responsable_inscripto', 'monotributista', 'exento', 'consumidor_final', 'no_responsable'])
    .nullable()
    .optional(),
  telefono: z
    .string()
    .trim()
    .max(50, 'Teléfono no puede superar 50 caracteres')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Email no tiene un formato válido')
    .max(120, 'Email no puede superar 120 caracteres')
    .optional()
    .or(z.literal('')),
  fecha_inicio_actividades: z
    .string()
    .refine(
      (v) => v === '' || v <= hoyIso(),
      'La fecha de inicio no puede ser futura',
    )
    .optional()
    .or(z.literal('')),
  convenio_2_porc: z.boolean(),
  estado: z.enum(['activa', 'inactiva', 'suspendida']),
  observaciones: z
    .string()
    .trim()
    .max(1000, 'Observaciones no pueden superar 1000 caracteres')
    .optional()
    .or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  cuit: '',
  razon_social: '',
  domicilio: '',
  condicion_iva: undefined,
  telefono: '',
  email: '',
  fecha_inicio_actividades: '',
  convenio_2_porc: true,
  estado: 'activa',
  observaciones: '',
};

export default function EmpresaForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nuevo';
  const navigate = useNavigate();

  const { data: empresa, isLoading: loadingEmpresa } = useEmpresa(isEdit ? id : undefined);
  const createMutation = useCreateEmpresa();
  const updateMutation = useUpdateEmpresa();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (isEdit && empresa) {
      reset({
        cuit: empresa.cuit,
        razon_social: empresa.razon_social,
        domicilio: empresa.domicilio ?? '',
        condicion_iva: (empresa.condicion_iva ?? undefined) as CondicionIva | undefined,
        telefono: empresa.telefono ?? '',
        email: empresa.email ?? '',
        fecha_inicio_actividades: empresa.fecha_inicio_actividades ?? '',
        convenio_2_porc: empresa.convenio_2_porc,
        estado: empresa.estado,
        observaciones: empresa.observaciones ?? '',
      });
    }
  }, [empresa, isEdit, reset]);

  const onSubmit = async (values: FormValues) => {
    const payload = {
      cuit: values.cuit,
      razon_social: values.razon_social,
      domicilio: values.domicilio || null,
      condicion_iva: (values.condicion_iva ?? null) as CondicionIva | null,
      telefono: values.telefono || null,
      email: values.email || null,
      fecha_alta: empresa?.fecha_alta ?? todayLocal(),
      fecha_inicio_actividades: values.fecha_inicio_actividades || null,
      convenio_2_porc: values.convenio_2_porc,
      estado: values.estado as EstadoEmpresa,
      observaciones: values.observaciones || null,
      delegacion_id: empresa?.delegacion_id ?? null,
    };

    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate('/empresas');
    } catch (err) {
      // El error se muestra abajo
    }
  };

  const mutation = isEdit ? updateMutation : createMutation;
  const apiError = mutation.error as Error | null;
  const apiErrorMessage = apiError?.message;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/empresas')}
          variant="text"
        >
          Volver
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ ml: 2 }}>
          {isEdit ? 'Editar empresa' : 'Nueva empresa'}
        </Typography>
      </Stack>

      {loadingEmpresa && isEdit && (
        <Card sx={{ p: 3 }}>
          <Stack spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Stack>
        </Card>
      )}

      {(!isEdit || !loadingEmpresa) && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              {apiErrorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiErrorMessage}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="cuit"
                    control={control}
                    render={({ field }) => (
                      <CuitInput
                        {...field}
                        required
                        fullWidth
                        label="CUIT"
                        error={!!errors.cuit}
                        helperText={errors.cuit?.message}
                        disabled={isEdit} // No permitir cambiar CUIT en edicion
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('razon_social')}
                    required
                    fullWidth
                    label="Razon social"
                    error={!!errors.razon_social}
                    helperText={errors.razon_social?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    {...register('domicilio')}
                    fullWidth
                    label="Domicilio fiscal"
                    error={!!errors.domicilio}
                    helperText={errors.domicilio?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="condicion_iva"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        select
                        fullWidth
                        label="Condicion IVA"
                        error={!!errors.condicion_iva}
                        helperText={errors.condicion_iva?.message}
                      >
                        <MenuItem value="">
                          <em>Sin especificar</em>
                        </MenuItem>
                        {CONDICION_IVA_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="estado"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        required
                        label="Estado"
                        error={!!errors.estado}
                        helperText={errors.estado?.message}
                      >
                        {ESTADO_EMPRESA_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('telefono')}
                    fullWidth
                    label="Telefono"
                    error={!!errors.telefono}
                    helperText={errors.telefono?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('email')}
                    type="email"
                    fullWidth
                    label="Email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    {...register('fecha_inicio_actividades')}
                    type="date"
                    fullWidth
                    label="Fecha inicio actividades"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.fecha_inicio_actividades}
                    helperText={errors.fecha_inicio_actividades?.message}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="convenio_2_porc"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Aplica convenio 2% (aporte sindical)"
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    {...register('observaciones')}
                    fullWidth
                    multiline
                    rows={3}
                    label="Observaciones"
                    error={!!errors.observaciones}
                    helperText={errors.observaciones?.message}
                  />
                </Grid>
              </Grid>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Button onClick={() => navigate('/empresas')} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  disabled={isSubmitting || (isEdit && !isDirty)}
                >
                  {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear empresa'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </form>
      )}
    </Box>
  );
}
